/**
 * New Relic Browser agent bootstrap.
 *
 * Configuration is driven by REACT_APP_NR_BROWSER_* env vars so dev and prod
 * can report to different New Relic accounts without touching the source.
 * Reli-Birds-dev values are set in the root run_relibirds.sh; CI sets the
 * prod values in .github/workflows/deploy.yaml.
 *
 * If any required env var is missing the agent is NOT initialised (no crash,
 * no stub) and a warning is logged — this lets local builds without the env
 * vars still run, at the cost of no browser telemetry.
 */
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

type NrEnv = {
  applicationId: string;
  licenseKey: string;
  accountId: string;
  trustKey: string;
  allowedOrigins: string[];
};

const parseEnv = (): NrEnv | null => {
  const applicationId = process.env.REACT_APP_NR_BROWSER_APPLICATION_ID;
  const licenseKey = process.env.REACT_APP_NR_BROWSER_LICENSE_KEY;
  const accountId = process.env.REACT_APP_NR_BROWSER_ACCOUNT_ID;
  const trustKey = process.env.REACT_APP_NR_BROWSER_TRUST_KEY;
  const originsRaw = process.env.REACT_APP_NR_BROWSER_DT_ORIGINS || '';

  if (!applicationId || !licenseKey || !accountId || !trustKey) {
    // eslint-disable-next-line no-console
    console.warn(
      '[newrelic-browser] REACT_APP_NR_BROWSER_* env vars missing — Browser agent not initialised.',
    );
    return null;
  }

  const allowedOrigins = originsRaw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  return { applicationId, licenseKey, accountId, trustKey, allowedOrigins };
};

export const initNewRelicBrowser = (): void => {
  const env = parseEnv();
  if (!env) return;

  const options = {
    info: {
      applicationID: env.applicationId,
      beacon: 'bam.nr-data.net',
      errorBeacon: 'bam.nr-data.net',
      licenseKey: env.licenseKey,
      sa: 1,
    },
    init: {
      ajax: {
        deny_list: ['bam.nr-data.net'],
      },
      browser_consent_mode: {
        enabled: false,
      },
      distributed_tracing: {
        enabled: true,
        cors_use_newrelic_header: true,
        cors_use_tracecontext_headers: true,
        allowed_origins: env.allowedOrigins,
      },
      performance: {
        capture_detail: false,
        capture_marks: false,
        capture_measures: true,
      },
      privacy: {
        cookies_enabled: true,
      },
      session_replay: {
        enabled: true,
        block_selector: '',
        mask_text_selector: '*',
        sampling_rate: 100.0,
        error_sampling_rate: 100.0,
        mask_all_inputs: true,
        collect_fonts: true,
        inline_images: false,
        inline_stylesheet: true,
        mask_input_options: {},
      },
    },
    loader_config: {
      accountID: env.accountId,
      agentID: env.applicationId,
      applicationID: env.applicationId,
      licenseKey: env.licenseKey,
      trustKey: env.trustKey,
    },
  };

  // Instantiating the loader side-effects: subscribes to window events, wraps
  // xhr/fetch, exposes window.newrelic. Must run once, as early as possible.
  // eslint-disable-next-line no-new
  new BrowserAgent(options);
};
